'use client';

import { useEffect, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Box, Button, CircularProgress, Typography } from '@/primitives';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import {
  buildSaleListingHref,
  buildSaleLotHref,
} from '@/lib/saleCataloguePaths';
import { useCurrentSociety } from '@/hooks/publicSearch/useCurrentSociety';
import { useGetLotAnimalEbvsQuery } from '@/publicSearch/graphql/queries.generated';
import { useSaleLotNavigation } from '@/hooks/publicSearch/sale-catalogue/useSaleLotNavigation';
import {
  buildSaleLotQueryOptions,
  isValidSaleLotIdentity,
  toSaleLotDetailData,
  type SaleLotIdentity,
} from '@/hooks/publicSearch/sale-catalogue/saleLotDetails';
import { parseAnimalDetailFieldsConfig } from '@/hooks/publicSearch/animal/animalEbvGrid';
import { useAnimalMedia } from '@/hooks/publicSearch/animal/useAnimalMedia';
import { pickPrimaryImageUrl } from '@/publicSearch/utils/pickPrimaryImageUrl';
import AnimalInfoCard from '@/components/publicSearch/animal/AnimalInfoCard';
import AnimalMediaTab from '@/components/publicSearch/animal/AnimalMediaTab';
import { PublicEntityNotFoundCard } from '@/components/publicSearch/PublicEntityNotFoundCard';
import usePublicSearchLimit from '@/components/publicSearch/heroSection/usePublicSearchLimit';
import RateLimitBanner from '@/components/publicSearch/RateLimitBanner';
import LotContextBar from './LotContextBar';

/** The standard detail components, loaded by lot identity when no animal exists. */
export function SaleLotDetailView({ saleId, lotNo }: SaleLotIdentity) {
  const society = useCurrentSociety();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { hasReachedSearchLimit } = usePublicSearchLimit();
  const identity = { saleId, lotNo };
  const valid = isValidSaleLotIdentity(identity);
  const { data, loading, error } = useGetLotAnimalEbvsQuery(
    buildSaleLotQueryOptions(identity, society.orgId),
  );
  const lot =
    data?.lotAnimalEbvs.items.find(
      (item) => item.saleId === Number(saleId) && item.lotNo === lotNo,
    ) ?? null;
  const navigation = useSaleLotNavigation({
    saleId: valid ? saleId : '',
    lotNo: valid ? lotNo : '',
    currentLot: lot,
  });
  const media = useAnimalMedia({
    orgId: society.orgId,
    lotContext: identity,
  });
  const listHref = buildSaleListingHref(
    society.societyCode ?? '',
    saleId,
    searchParams,
  );
  const linkedAnimalId =
    lot?.animalId && lot.animalId > 0 ? lot.animalId : null;

  // A saved lot URL continues to work if the lot later gains a registered animal.
  useEffect(() => {
    if (!linkedAnimalId || !society.societyCode) return;
    router.replace(
      buildSaleLotHref(society.societyCode, {
        saleId,
        animalId: linkedAnimalId,
        lotNo,
        searchParams,
      }),
    );
  }, [
    linkedAnimalId,
    lotNo,
    router,
    saleId,
    searchParams,
    society.societyCode,
  ]);

  const detailFieldsConfig = useMemo(() => {
    const fields = parseAnimalDetailFieldsConfig(
      society.settings,
      SOCIETY_SETTING_TYPE.LOT_DETAIL_FIELDS,
    ).filter(
      // The shared lot header already renders vendor and formatted prices.
      ({ field }) => !/^(vendorsname|askingprice|saleprice)$/i.test(field),
    );
    // i4 places the lot name in the heading; the standard card shows it as a field.
    return fields.some((field) => /^(lot)?animalname$/i.test(field.field))
      ? fields
      : [
          { field: 'AnimalName', label: 'Name', display: 1, order: '0' },
          ...fields,
        ];
  }, [society.settings]);

  if (society.loading || loading || linkedAnimalId)
    return <CircularProgress aria-label="Loading lot details" />;
  if (hasReachedSearchLimit && !lot) return <RateLimitBanner />;
  if (error) {
    return (
      <Box role="alert" sx={{ p: 2 }}>
        <Typography>We could not load this lot. Please try again.</Typography>
        <Button onClick={() => router.push(listHref)}>
          Back to Lot Listing
        </Button>
      </Box>
    );
  }
  if (!valid || !lot) {
    return (
      <PublicEntityNotFoundCard
        entityLabel="lot"
        entityId={lotNo.trim()}
        listHref={listHref}
        listLabel="Back to Lot Listing"
      />
    );
  }

  const detail = toSaleLotDetailData(lot);
  const primaryImage = pickPrimaryImageUrl(lot.documents);
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      <LotContextBar
        orgCode={society.societyCode ?? ''}
        saleId={saleId}
        lotNo={lotNo}
        navigation={navigation}
      />
      <AnimalInfoCard
        animalViewData={{
          animalData: detail,
          animalDisplayName: detail.animalName ?? undefined,
          animalImgUrls: primaryImage ? [primaryImage] : [],
          breeder: lot.breederId
            ? {
                additionalInfo: {
                  id: lot.breederId,
                  ident: lot.breederIdent ?? '',
                  name: lot.breederName ?? '',
                  detailsAvailFlag: lot.breederDetailsAvailFlag ?? null,
                },
              }
            : undefined,
          owner: lot.currentOwnerId
            ? {
                additionalInfo: {
                  id: lot.currentOwnerId,
                  ident: lot.currentOwnerIdent ?? '',
                  name: lot.currentOwnerName ?? '',
                  detailsAvailFlag: lot.currentOwnerDetailsAvailFlag ?? null,
                },
              }
            : undefined,
          isLoading: false,
          isLoadingImg: false,
        }}
        orgId={society.orgId}
        societyCode={society.societyCode}
        detailFieldsConfig={detailFieldsConfig}
        isInSaleCatalogueContext
        onBack={() => router.push(listHref)}
      />
      {(media.loading || media.items.length > 0) && (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Media</Typography>
          <AnimalMediaTab
            items={media.items}
            loading={media.loading}
            animalName={detail.animalName}
          />
        </Box>
      )}
    </Box>
  );
}
